import LZString from 'lz-string';
import { OrgNavigationFilters } from './OrgNavigationContent';
import { createDefaultOrgNavigationFilters, toSerializableOrgNavigationFilters } from './orgNavigationFiltersStorage';
import { OrgNavigationDateRangeType } from './OrgNavigationDateRange';

export const ORG_OVERVIEW_PATH = '/dashboard/org/:orgId';

const ORG_OVERVIEW_QUERY_PARAMS = {
    filters: 'ov2Filters',
    dateRangeType: 'ov2DateRangeType',
    startDate: 'ov2Start',
    endDate: 'ov2End',
    metrics: 'ov2Metrics',
    workflowApps: 'ov2Apps',
    destinations: 'ov2Destinations',
    buildBuckets: 'ov2BuildBuckets',
    platforms: 'ov2Platforms',
    orgTags: 'ov2Tags',
    users: 'ov2Users',
    insights: 'ov2Insights',
} as const;

const isValidDateRangeType = (value: string | null): value is OrgNavigationDateRangeType => {
    return !!value && Object.values(OrgNavigationDateRangeType).includes(value as OrgNavigationDateRangeType);
};

const parseCsvParam = (value: string | null): string[] => {
    if (!value) {
        return [];
    }

    return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
};

const toRecord = (values: string[]): Record<string, string> => {
    return values.reduce(
        (acc, value) => {
            acc[value] = value;
            return acc;
        },
        {} as Record<string, string>,
    );
};

const encodeOrgOverviewFilters = (filters: unknown): string => {
    const json = JSON.stringify(filters);
    return LZString.compressToEncodedURIComponent(json);
};

const decodeOrgOverviewFilters = (value: string): OrgNavigationFilters | null => {
    try {
        const json = LZString.decompressFromEncodedURIComponent(value);
        if (!json) {
            return null;
        }

        return JSON.parse(json) as OrgNavigationFilters;
    } catch {
        return null;
    }
};

export const parseOrgNavigationFiltersFromSearch = (search: string): OrgNavigationFilters => {
    const params = new URLSearchParams(search);
    const defaults = createDefaultOrgNavigationFilters();
    const encodedFilters = params.get(ORG_OVERVIEW_QUERY_PARAMS.filters);
    const decodedFilters = encodedFilters ? decodeOrgOverviewFilters(encodedFilters) : null;

    if (decodedFilters) {
        return {
            ...defaults,
            ...decodedFilters,
        };
    }

    const parsedDateRangeType = params.get(ORG_OVERVIEW_QUERY_PARAMS.dateRangeType);
    const startDate = params.get(ORG_OVERVIEW_QUERY_PARAMS.startDate);
    const endDate = params.get(ORG_OVERVIEW_QUERY_PARAMS.endDate);
    const selectedMetrics = parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.metrics));
    const shouldGenerateInsights = params.get(ORG_OVERVIEW_QUERY_PARAMS.insights);

    return {
        ...defaults,
        selectedDateRangeType:
            isValidDateRangeType(parsedDateRangeType) ? parsedDateRangeType : defaults.selectedDateRangeType,
        selectedDateRange:
            startDate && endDate ?
                {
                    start: startDate,
                    end: endDate,
                }
            :   defaults.selectedDateRange,
        selectedMetrics: selectedMetrics.length ? selectedMetrics : defaults.selectedMetrics,
        selectedWorkflowApps: toRecord(parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.workflowApps))),
        selectedDestinations: toRecord(parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.destinations))),
        selectedBuildBuckets: toRecord(parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.buildBuckets))),
        selectedPlatforms: toRecord(parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.platforms))),
        selectedOrgTags: toRecord(parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.orgTags))),
        selectedUsers: toRecord(parseCsvParam(params.get(ORG_OVERVIEW_QUERY_PARAMS.users))),
        shouldGenerateInsights:
            shouldGenerateInsights === null ? defaults.shouldGenerateInsights : shouldGenerateInsights === 'true',
    };
};

export const hasOrgNavigationFiltersInSearch = (search: string): boolean => {
    const params = new URLSearchParams(search);

    return Object.values(ORG_OVERVIEW_QUERY_PARAMS).some((key) => params.has(key));
};

export const buildOrgNavigationSearchParams = (filters: OrgNavigationFilters, search: string): URLSearchParams => {
    const params = new URLSearchParams(search);
    const encodedFilters = encodeOrgOverviewFilters(toSerializableOrgNavigationFilters(filters));

    params.set(ORG_OVERVIEW_QUERY_PARAMS.filters, encodedFilters);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.dateRangeType);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.startDate);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.endDate);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.metrics);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.workflowApps);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.destinations);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.buildBuckets);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.platforms);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.orgTags);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.users);
    params.delete(ORG_OVERVIEW_QUERY_PARAMS.insights);

    return params;
};

export const areSerializableFiltersEqual = (left: OrgNavigationFilters, right: OrgNavigationFilters): boolean => {
    const areRecordsEqual = (leftRecord: Record<string, string>, rightRecord: Record<string, string>) => {
        const leftKeys = Object.keys(leftRecord).sort();
        const rightKeys = Object.keys(rightRecord).sort();

        return (
            leftKeys.length === rightKeys.length &&
            leftKeys.every((key, index) => key === rightKeys[index] && leftRecord[key] === rightRecord[key])
        );
    };
    return (
        left.selectedDateRangeType === right.selectedDateRangeType &&
        left.selectedDateRange.start === right.selectedDateRange.start &&
        left.selectedDateRange.end === right.selectedDateRange.end &&
        left.selectedMetrics.join(',') === right.selectedMetrics.join(',') &&
        areRecordsEqual(left.customBucketSizes, right.customBucketSizes) &&
        areRecordsEqual(left.selectedWorkflowApps, right.selectedWorkflowApps) &&
        areRecordsEqual(left.selectedDestinations, right.selectedDestinations) &&
        areRecordsEqual(left.selectedPlatforms, right.selectedPlatforms) &&
        areRecordsEqual(left.selectedOrgTags, right.selectedOrgTags) &&
        areRecordsEqual(left.selectedUsers, right.selectedUsers) &&
        areRecordsEqual(left.selectedBuildBuckets, right.selectedBuildBuckets) &&
        left.selectedReleaseTypes === right.selectedReleaseTypes &&
        left.shouldGenerateInsights === right.shouldGenerateInsights
    );
};
